import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { SET_LANGUAGE } from '../redux/languageReducer';
import { useState } from 'react';
import './Navigation.css';

const Navigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const language = useSelector((state) => state.language);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleDemo = () => {
    navigate('/pricing');
  };

  const handleLanguageChange = (lang) => {
    dispatch({ type: SET_LANGUAGE, payload: lang });
  };

  const languages = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'ar', label: 'AR' }
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <img className="w-28" src="/Raqib-dark.png" alt="RAQIB Logo" />
        </div>

        {/* Center Navigation - Hidden on mobile */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `transition-colors font-medium text-sm ${
                isActive ? "text-teal-600" : "text-gray-700 hover:text-teal-600"
              }`
            }
          >
            {language === "ar" ? "الرئيسية" : language === "fr" ? "Accueil" : "Home"}
          </NavLink>
          <NavLink
            to="/features"
            className={({ isActive }) =>
              `transition-colors font-medium text-sm ${
                isActive ? "text-teal-600" : "text-gray-700 hover:text-teal-600"
              }`
            }
          >
            {language === "ar" ? "الميزات" : language === "fr" ? "Fonctionnalités" : "Features"}
          </NavLink>
          <NavLink
            to="/solutions"
            className={({ isActive }) =>
              `transition-colors font-medium text-sm ${
                isActive ? "text-teal-600" : "text-gray-700 hover:text-teal-600"
              }`
            }
          >
            {language === "ar" ? "الحلول" : language === "fr" ? "Solutions" : "Solutions"}
          </NavLink>
          <NavLink
            to="/pricing"
            className={({ isActive }) =>
              `transition-colors font-medium text-sm ${
                isActive ? "text-teal-600" : "text-gray-700 hover:text-teal-600"
              }`
            }
          >
            {language === "ar" ? "الأسعار" : language === "fr" ? "Tarification" : "Pricing"}
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `transition-colors font-medium text-sm ${
                isActive ? "text-teal-600" : "text-gray-700 hover:text-teal-600"
              }`
            }
          >
            {language === "ar" ? "اتصل بنا" : language === "fr" ? "Contact" : "Contact"}
          </NavLink>
        </nav>

        {/* Right Section - Desktop */}
        <div className="hidden md:flex items-center gap-4">
          {/* Language Selector */}
          <div className="flex gap-2 border-l border-gray-300 pl-4">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`px-2.5 py-1.5 rounded font-medium text-sm transition-colors ${
                  language === lang.code
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-600 hover:text-teal-600'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* Demo Button */}
          <button
            onClick={handleDemo}
            className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors text-sm whitespace-nowrap"
          >
            {language === "ar" ? "عرض توضيحي" : language === "fr" ? "Démo" : "Demo"}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-3">
          {/* Mobile Language Selector */}
          <div className="flex gap-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  language === lang.code
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-600'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-teal-600"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Visible only on mobile */}
      {mobileMenuOpen && (
        <nav className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="container mx-auto px-4 py-3 flex flex-col gap-3">
            <NavLink
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded transition-colors font-medium ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-700 hover:bg-gray-100 hover:text-teal-600"
                }`
              }
            >
              {language === "ar" ? "الرئيسية" : language === "fr" ? "Accueil" : "Home"}
            </NavLink>
            <NavLink
              to="/features"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded transition-colors font-medium ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-700 hover:bg-gray-100 hover:text-teal-600"
                }`
              }
            >
              {language === "ar" ? "الميزات" : language === "fr" ? "Fonctionnalités" : "Features"}
            </NavLink>
            <NavLink
              to="/solutions"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded transition-colors font-medium ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-700 hover:bg-gray-100 hover:text-teal-600"
                }`
              }
            >
              {language === "ar" ? "الحلول" : language === "fr" ? "Solutions" : "Solutions"}
            </NavLink>
            <NavLink
              to="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded transition-colors font-medium ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-700 hover:bg-gray-100 hover:text-teal-600"
                }`
              }
            >
              {language === "ar" ? "الأسعار" : language === "fr" ? "Tarification" : "Pricing"}
            </NavLink>
            <NavLink
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 px-3 rounded transition-colors font-medium ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-700 hover:bg-gray-100 hover:text-teal-600"
                }`
              }
            >
              {language === "ar" ? "اتصل بنا" : language === "fr" ? "Contact" : "Contact"}
            </NavLink>
            <button
              onClick={() => {
                handleDemo();
                setMobileMenuOpen(false);
              }}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm mt-4"
            >
              {language === "ar" ? "عرض توضيحي" : language === "fr" ? "Démo" : "Demo"}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}

export default Navigation;

